import { AlertType, PartyData, Season, TimeSlot } from '@types';
import {
  AlertModal,
  Card,
  EmptyState,
  LoadingSpinner,
  SectionHeader,
} from '_atoms';
import { SeasonDetailsService, TicketService } from '_services';
import HelperMethods from '_utils/HelperMethods';
import React, { useCallback, useEffect, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import {
  MdArrowBack,
  MdCelebration,
  MdEvent,
  MdSchedule,
} from 'react-icons/md';

interface PartySelectSessionDateTimeProps {
  selectedTimeSlot?: TimeSlot;
  onNext: (data?: Partial<PartyData>) => void;
  onPrevious?: () => void;
  onStepComplete: (data: Partial<PartyData>) => void;
}

export const PartySelectSessionDateTime: React.FC<
  PartySelectSessionDateTimeProps
> = ({ selectedTimeSlot, onNext, onPrevious, onStepComplete }) => {
  const [selectedDate, setSelectedDate] = useState<string>(
    selectedTimeSlot?.date || new Date().toISOString(),
  );
  const [currentSelectedTimeSlot, setCurrentSelectedTimeSlot] = useState<
    TimeSlot | undefined
  >(selectedTimeSlot);
  const [settings, setSettings] = useState<Season>({
    id: '',
    startDate: '',
    endDate: '',
    isTicketPurchasesEnabled: false,
    isSeasonPassPurchasesEnabled: false,
    isPartyPackagePurchasesEnabled: false,
    isSkatingLessonPurchasesEnabled: false,
    isHockeyLessonPurchasesEnabled: false,
    sessionTimes: [],
  });
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: AlertType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  // Load current season details on page load
  useEffect(() => {
    const loadCurrentSeason = async () => {
      setIsLoadingInitial(true);
      try {
        const responseData =
          (await SeasonDetailsService.getCurrentSeasonDetails()) as unknown as Season;

        if (
          responseData &&
          (responseData.startDate ||
            responseData.endDate ||
            responseData.sessionTimes.length > 0)
        ) {
          setSettings(responseData);
        } else {
          showAlert(
            'info',
            'Info',
            'There are currently no active season settings.',
          );
        }
      } catch (error) {
        console.error('Error loading current season:', error);
        showAlert('error', 'Error', 'Failed to load current season settings.');
      } finally {
        setIsLoadingInitial(false);
      }
    };

    loadCurrentSeason();
  }, []);

  const loadTimeSlotsForDate = useCallback(async (date: string) => {
    setIsLoadingTimeSlots(true);
    try {
      // Check if the selected date is today - if so, don't load any timeslots
      const today = new Date().toDateString();
      const selectedDateString = new Date(date).toDateString();

      if (today === selectedDateString) {
        setTimeSlots([]);
        setIsLoadingTimeSlots(false);
        return;
      }

      const responseData = (await TicketService.getTimeSlotsForDate(
        date,
      )) as unknown as TimeSlot[];

      // Filter out time slots where partiesLimit is 0
      const filteredTimeSlots = (responseData || []).filter(
        timeSlot => timeSlot.partiesLimit !== 0,
      );

      setTimeSlots(filteredTimeSlots);
    } catch (error) {
      console.error('Error loading time slots:', error);
      showAlert(
        'error',
        'Error',
        'Failed to load party session times for the selected date.',
      );
      setTimeSlots([]);
    } finally {
      setIsLoadingTimeSlots(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) {
      loadTimeSlotsForDate(selectedDate);
    } else {
      setTimeSlots([]);
    }
  }, [selectedDate, loadTimeSlotsForDate]);

  const showAlert = (type: AlertType, title: string, message: string) => {
    setAlertModal({ isOpen: true, type, title, message });
  };

  const closeAlert = () => {
    setAlertModal({ isOpen: false, type: 'info', title: '', message: '' });
  };

  const handleContinue = () => {
    if (selectedDate && currentSelectedTimeSlot) {
      onStepComplete({ selectedTimeSlot: currentSelectedTimeSlot });
      onNext({ selectedTimeSlot: currentSelectedTimeSlot });
    }
  };

  const NotInSeason = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <Card className="w-full max-w-md justify-center items-center border-none">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
            <MdEvent className="w-8 h-8 text-gray-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Party Bookings Not Available
            </h2>
            <p className="text-gray-600">
              Party package purchases are currently not available. Please check
              back later.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {!settings.isPartyPackagePurchasesEnabled && !isLoadingInitial ? (
        <NotInSeason />
      ) : (
        <>
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-gray-900">Book A Date</h2>
            <p className="text-gray-600 text-lg">
              Choose when you'd like to have your party
            </p>
          </div>

          {isLoadingInitial ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Date Selection */}
                <div className="lg:w-1/2">
                  {settings && settings.startDate && (
                    <Card className="w-full">
                      <div className="p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                          <MdEvent className="mr-2 text-blue-600" />
                          Select Date
                        </h3>
                        <DayPicker
                          mode="single"
                          startMonth={
                            settings.startDate
                              ? new Date(settings.startDate)
                              : new Date()
                          }
                          selected={
                            selectedDate ? new Date(selectedDate) : undefined
                          }
                          onSelect={e =>
                            setSelectedDate(e?.toISOString() || '')
                          }
                          modifiers={{
                            disabled: [
                              // Disable past dates and today (before tomorrow)
                              {
                                before: new Date(
                                  new Date().setHours(0, 0, 0, 0) +
                                    24 * 60 * 60 * 1000,
                                ),
                              },
                              // Disable dates outside season if season dates are set
                              ...(settings.startDate && settings.endDate
                                ? [
                                    { before: new Date(settings.startDate) },
                                    { after: new Date(settings.endDate) },
                                  ]
                                : []),
                            ],
                          }}
                          footer={
                            <div className="text-sm text-gray-500 mt-4 p-3 bg-gray-50 rounded-lg">
                              {settings.startDate && settings.endDate && (
                                <div className="text-center">
                                  <span className="font-medium">
                                    Season runs from
                                  </span>
                                  <br />
                                  {new Date(
                                    settings.startDate,
                                  ).toLocaleDateString()}{' '}
                                  to{' '}
                                  {new Date(
                                    settings.endDate,
                                  ).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          }
                        />
                      </div>
                    </Card>
                  )}
                </div>

                {/* Time Slots */}
                <div className="lg:w-1/2">
                  {!selectedDate ? (
                    <Card className="w-full h-full flex items-center justify-center">
                      <EmptyState
                        title="No Date Selected"
                        description="Please select a date to view party session times."
                        icon={MdEvent}
                      />
                    </Card>
                  ) : (
                    <div className="w-full p-2">
                      <SectionHeader
                        title={`Party Sessions for ${new Date(selectedDate).toLocaleDateString()}`}
                        icon={MdSchedule}
                      />

                      {isLoadingTimeSlots ? (
                        <div className="flex items-center justify-center py-8">
                          <LoadingSpinner />
                        </div>
                      ) : timeSlots.length === 0 ? (
                        <EmptyState
                          title="No Party Sessions Found"
                          description={`No party sessions found for ${new Date(selectedDate).toLocaleDateString()}.`}
                          icon={MdSchedule}
                        />
                      ) : (
                        <div className="space-y-3 mt-4 w-full">
                          {timeSlots.map((timeSlot: any) => (
                            <div
                              key={timeSlot.id}
                              onClick={() =>
                                !timeSlot.isDisabled &&
                                timeSlot.partiesLimit > 0 &&
                                setCurrentSelectedTimeSlot(timeSlot)
                              }
                              className={
                                timeSlot.isDisabled
                                  ? 'cursor-not-allowed'
                                  : 'cursor-pointer'
                              }
                            >
                              <div
                                className={`flex w-full gap-2 sm:gap-4 justify-between border rounded-lg p-3 sm:p-2 transition-all duration-200 ${
                                  timeSlot.isDisabled
                                    ? 'bg-gray-100 border-gray-300 opacity-60'
                                    : currentSelectedTimeSlot?.id ===
                                        timeSlot.id
                                      ? 'bg-blue-50 border-blue-300 shadow-md hover:shadow-md'
                                      : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'
                                }`}
                              >
                                <div className="flex items-center">
                                  <div className="w-6 h-6 bg-blue-100 mr-2 rounded-full flex items-center justify-center">
                                    <MdSchedule className="w-4 h-4  text-blue-600" />
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-900 text-sm sm:text-base">
                                      {HelperMethods.formatTime12Hour(
                                        timeSlot.timeSlot,
                                      )}
                                    </div>
                                    <div className="text-xs sm:text-sm text-gray-500">
                                      Party Time
                                    </div>
                                  </div>
                                </div>

                                <div className="text-right">
                                  {timeSlot.isDisabled ? (
                                    <div className="text-center">
                                      <div className="text-xs text-red-600 font-medium">
                                        {timeSlot.partiesLimit > 0
                                          ? 'Tickets only available at Ticket Booth'
                                          : 'SOLD OUT'}
                                      </div>
                                      <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                                        <MdCelebration className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                        <span className="font-semibold text-gray-900 text-sm sm:text-base">
                                          {timeSlot.partiesLimit < 1
                                            ? 0
                                            : timeSlot.partiesLimit}
                                        </span>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                                        <MdCelebration className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                        <span className="font-semibold text-gray-900 text-sm sm:text-base">
                                          {timeSlot.partiesLimit < 1
                                            ? 0
                                            : timeSlot.partiesLimit}
                                        </span>
                                      </div>
                                      <div className="text-xs sm:text-sm text-gray-500">
                                        Available
                                      </div>
                                      {timeSlot.partiesLimit < 1 ? (
                                        <div className="text-xs text-red-600 font-bold mt-1">
                                          SOLD OUT
                                        </div>
                                      ) : (
                                        timeSlot.partiesLimit < 2 && (
                                          <div className="text-xs text-red-600 font-medium mt-1">
                                            Hurry up! Almost sold out!
                                          </div>
                                        )
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-6">
                {onPrevious && (
                  <button
                    onClick={onPrevious}
                    className="inline-flex items-center px-4 py-3 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                  >
                    <MdArrowBack className="mr-2" />
                    Back
                  </button>
                )}
                <div className="flex-1" />
                <button
                  onClick={handleContinue}
                  disabled={
                    !currentSelectedTimeSlot ||
                    currentSelectedTimeSlot.partiesLimit < 1
                  }
                  className="inline-flex items-center px-6 sm:px-8 py-4 sm:py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md text-base sm:text-sm"
                >
                  Continue
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};
